// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { CROP_STATUS, shortenDensityUnit } from '@seedplanner/shared-utils';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { formatDuration, shortenWeightUnit } from '../../utils';
import { getID } from '../../utils';
import { StackedTitle, Table } from '../common/table';
import type { ColumnType } from '../common/table/Table';
import CropIdLink from './CropIdLink';
import CropOptions from './CropOptions';
import { getSoakStatus } from './getSoakStatus';

interface Props {
  readonly crops: {
    readonly id: string;
    readonly crop_name: string;
    readonly total_grow_time: number;
    readonly yield_per_tray: string;
    readonly yield_unit: string;
    readonly sowing_density?: string | null;
    readonly sow_density_unit: string;
    readonly status: string;
    readonly steps: {
      readonly edges: Array<{
        readonly node: {
          readonly name: string;
          readonly time_to_start_after_first_step: number;
        };
      }>;
    };
  }[];
  readonly userCanEditCrops: boolean;
}

export default function CropTable({ crops, userCanEditCrops }: Props) {
  const growTimeVals: string[] = [];
  const stepCounts: number[] = [];
  const yieldValues: string[] = [];
  const soakStepValues: string[] = [];
  const sowDensityValues: string[] = [];
  const stackedLinks: React.ReactElement[] = [];
  const normalLinks: React.ReactElement[] = [];
  const cropsCopy = [...crops];
  cropsCopy.sort((a, b) => a.crop_name.localeCompare(b.crop_name));
  for (const crop of cropsCopy) {
    growTimeVals.push(formatDuration(crop.total_grow_time));
    const steps = crop.steps.edges.map((step) => step.node);
    const stepCount = steps.length;

    const soakStepValue = getSoakStatus(steps);
    soakStepValues.push(soakStepValue);

    stepCounts.push(stepCount);
    yieldValues.push(`${crop.yield_per_tray} ${shortenWeightUnit(crop.yield_unit)}`);
    const sowDensity =
      crop.sowing_density == null || crop.sow_density_unit == null
        ? ''
        : `${crop.sowing_density} ${shortenDensityUnit(crop.sow_density_unit)}`;
    sowDensityValues.push(sowDensity);

    const link = <CropIdLink id={getID(crop.id)} title={crop.crop_name} name={crop.crop_name} />;
    normalLinks.push(link);

    stackedLinks.push(
      <StackedTitle
        title={link}
        subtitle={`Yield: ${crop.yield_per_tray} ${shortenWeightUnit(crop.yield_unit)} - Grow: ${formatDuration(
          crop.total_grow_time,
        )} - Sow: ${sowDensity}${soakStepValue === '' ? '' : ` - ${soakStepValue}`}`}
      />,
    );
  }
  const columns: ColumnType[] = [
    {
      name: 'Name',
      values: normalLinks,
    },
    {
      name: 'Steps',
      values: stepCounts,
      numeric: true,
      visibleAt: 'lg',
    },
    {
      name: 'Yield Per Tray',
      smallName: 'Yield',
      values: yieldValues,
    },
    {
      name: 'Soak',
      values: soakStepValues,
      visibleAt: 'sm',
    },
    {
      name: 'Sow Density',
      values: sowDensityValues,
      visibleAt: 'sm',
    },
    {
      name: 'Grow Time',
      smallName: 'Grow',
      values: growTimeVals,
      numeric: true,
    },
  ];
  if (userCanEditCrops) {
    columns.push({
      name: 'Actions',
      smallName: '',
      numeric: true,
      flex: '0 0 auto',
      values: cropsCopy.map((crop) => (
        <CropOptions
          cropName={crop.crop_name}
          key={crop.id}
          useMoreIcon
          cropId={crop.id}
          isArchived={crop.status === CROP_STATUS.ARCHIVED}
        />
      )),
    });
  }

  return <Table columns={columns} />;
}
